import { AbstractConnector } from './AbstractConnector';
import { ProviderType } from '@dcl/schemas';
import { getConfiguration } from '../configuration';
export class WalletConnectV2Connector extends AbstractConnector {
    constructor(desiredChainId) {
        super({
            supportedChainIds: (() => {
                const { chains, optionalChains } = WalletConnectV2Connector.configuration.chains[desiredChainId];
                return [...chains, ...optionalChains];
            })()
        });
        this.desiredChainId = desiredChainId;
        this.activate = async () => {
            const provider = await import('@walletconnect/ethereum-provider').then(module => {
                const { chains, optionalChains } = WalletConnectV2Connector.configuration.chains[this.desiredChainId];
                return module.default.init({
                    projectId: WalletConnectV2Connector.configuration.projectId,
                    rpcMap: WalletConnectV2Connector.configuration.urls,
                    chains,
                    optionalChains,
                    showQrModal: true,
                    // Decentraland's RPCs don't support the `test` method used for the ping.
                    disableProviderPing: true,
                    qrModalOptions: {
                        themeVariables: {
                            // Display the WC modal over other Decentraland UI's modals.
                            // Won't be visible without this.
                            '--wcm-z-index': '3000'
                        }
                    },
                    // Methods and events based on what is used on the decentraland dapps and the ethereum-provider lib found at:
                    // https://github.com/WalletConnect/walletconnect-monorepo/blob/v2.0/providers/ethereum-provider/src/constants/rpc.ts
                    // If the wallet doesn't support non optional methods, it will not allow the connection.
                    methods: ['eth_sendTransaction', 'personal_sign'],
                    optionalMethods: [
                        'eth_accounts',
                        'eth_requestAccounts',
                        'eth_sign',
                        'eth_signTypedData_v4',
                        'wallet_switchEthereumChain',
                        'wallet_addEthereumChain'
                    ],
                    events: ['chainChanged', 'accountsChanged'],
                    optionalEvents: ['disconnect']
                });
            });
            const accounts = await provider.enable();
            provider.on('accountsChanged', this.handleAccountsChanged);
            provider.on('chainChanged', this.handleChainChanged);
            provider.on('disconnect', this.handleDisconnect);
            this.provider = provider;
            return {
                chainId: provider.chainId,
                account: accounts[0],
                provider
            };
        };
        this.getProvider = async () => {
            if (!this.provider) {
                throw new Error('Provider is undefined');
            }
            return this.provider;
        };
        this.getChainId = async () => {
            if (!this.provider) {
                throw new Error('Provider is undefined');
            }
            return this.provider.chainId;
        };
        this.getAccount = async () => {
            if (!this.provider) {
                throw new Error('Provider is undefined');
            }
            return this.provider.accounts[0];
        };
        this.getWalletName = () => {
            return this.provider?.session?.peer.metadata.name;
        };
        this.deactivate = () => undefined;
        this.close = async () => {
            if (!this.provider) {
                return;
            }
            return this.provider
                .removeListener('accountsChanged', this.handleAccountsChanged)
                .removeListener('chainChanged', this.handleChainChanged)
                .removeListener('disconnect', this.handleDisconnect)
                .disconnect();
        };
        this.handleAccountsChanged = (accounts) => {
            this.emitUpdate({ account: accounts[0] });
        };
        this.handleChainChanged = (chainId) => {
            this.emitUpdate({ chainId });
        };
        this.handleDisconnect = () => {
            if (!this.provider) {
                throw new Error('Provider is undefined');
            }
            return this.emitDeactivate();
        };
    }
}
WalletConnectV2Connector.configuration = getConfiguration()[ProviderType.WALLET_CONNECT_V2];
WalletConnectV2Connector.clearStorage = (storage) => {
    storage.removeRegExp(new RegExp('^wc@2:'));
};
//# sourceMappingURL=WalletConnectV2Connector.js.map